import { useSnackbar } from 'notistack';
import { useState, useEffect, useRef } from 'react';
import { User, Lock, Settings, Camera, Loader2 } from 'lucide-react';

import actionEditarPerfil from 'src/actions/usuarios/actionEditarPerfil';
import actionEditarSenha from 'src/actions/usuarios/actionEditarSenhaUsuario';
import actionDetalhesUsuario from 'src/actions/usuarios/actionDetalhesUsuario';
import actionUploadAvatar from 'src/actions/usuarios/actionUploadAvatar';

import PerfilAlterarSenha from 'src/components/_dashboard/perfil/PerfilAlterarSenha';
import PerfilGeral from 'src/components/_dashboard/perfil/PerfilGeral';
import useAuth from 'src/hooks/useAuth';
import Preferencias from 'src/components/_dashboard/perfil/Preferencias';
import { soNumeros } from 'src/utils/functions';
import v2_actionEditarUsuario from 'src/actions/v2/usuarios/v2_actionEditarUsuario';
import { PERFIL_COMPONENT_DESIGN } from './PerfilComponent.design';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/@/components/ui/tabs"

export default function PerfilComponent() {
  const { enqueueSnackbar } = useSnackbar();
  const [nome, setNome] = useState('');
  const [sobrenome, setSobrenome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [usuario, setUsuario] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const soundNotification = localStorage.getItem('soundNotification');
  const { updateProfile, user, cliente } = useAuth();
  const [mensagemIntervalo, setMensagemIntervalo] = useState(null);
  const [isModificado, setIsModificado] = useState(false);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    buscarDetalhesUsuario()
  }, []);

  const handleEditarSenha = () => {
    setIsLoading(true)
    if (password === repeatPassword) {
      actionEditarSenha({ usuarioId: user.id, password: password, repeatPassword: repeatPassword }).then(() => {
        enqueueSnackbar('Senha alterada com sucesso', { variant: 'success' });
      }).catch(() => {
        enqueueSnackbar('Erro ao alterar senha', { variant: 'error' });
      })
    } else {
      enqueueSnackbar('Senhas não conferem', { variant: 'error' });
    }
  }

  function maskUsername(str) {
    return str.split(' ').join('')
  }

  const handleEditarPerfil = () => {
    setIsLoading(true);

    let data = {
      usuarioId: user.id,
      firstName: nome,
      lastName: sobrenome,
      email,
      telefone: `55${soNumeros(telefone)}`,
      username: maskUsername(usuario),
      nomeFantasia
    }

    actionEditarPerfil(data).then(res => {
      if (res.data.error === true) {
        enqueueSnackbar(res.data.mensagem, { variant: 'error' });
      } else {
        updateProfile({
          firstName: nome,
          lastName: sobrenome,
          email,
          username: maskUsername(usuario),
          nomeFantasia
        });
        enqueueSnackbar(res.data.mensagem, { variant: 'success' });
      }
    }).catch(() => {
      enqueueSnackbar('Erro ao alterar perfil', { variant: 'error' });
    }).finally(() => {
      setIsLoading(false);
    })
  }

  const changeSoundNotification = () => {
    if (soundNotification === 'true') {
      localStorage.setItem('soundNotification', "false");
      window.location.reload();
    } else if (soundNotification === 'false') {
      localStorage.setItem('soundNotification', "true");
      window.location.reload();
    }
  };

  const handleUploadAvatar = async (file) => {
    setAvatarLoading(true);
    try {
      const res = await actionUploadAvatar(user.id, file);
      if (!res.data.error) {
        setAvatar(res.data.data.avatar);
        updateProfile({ avatar: res.data.data.avatar });
        enqueueSnackbar('Foto atualizada com sucesso', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar foto', { variant: 'error' });
    } finally {
      setAvatarLoading(false);
    }
  }

  async function handelEditarPreferencias() {
    setIsLoading(true);
    try {
      let data = {
        "scope": {
          "config.messageStandby": mensagemIntervalo,
          "config.betaConfig": config.betaConfig
        }
      }
      await v2_actionEditarUsuario(user.id, data);
      enqueueSnackbar('Alterado com sucesso', { variant: 'success' });
    } catch (erro) {
      setIsLoading(false);
      setTimeout(() => {
        enqueueSnackbar('Erro ao alterar perfil', { variant: 'error' });
      }, 1000);
    }
  }

  const buscarDetalhesUsuario = async () => {
    try {
      const res = await actionDetalhesUsuario(user.id);
      if (res.data.data) {
        let dataUsuario = res.data.data;
        const rawPhone = dataUsuario.telefone;
        setTelefone(rawPhone ? rawPhone.substring(2) : '');
        setAvatar(dataUsuario.avatar || null);
        setNome(dataUsuario.firstName);
        setSobrenome(dataUsuario.lastName);
        setEmail(dataUsuario.email);
        setUsuario(dataUsuario.username);
        setNomeFantasia(dataUsuario.nomeFantasia);
        setMensagemIntervalo(dataUsuario?.config?.messageStandby);
        setConfig(dataUsuario.config);
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <div className={PERFIL_COMPONENT_DESIGN.page.container}>
      {/* Header */}
      <div className={PERFIL_COMPONENT_DESIGN.page.header.container}>
        <div>
          <h1 className={PERFIL_COMPONENT_DESIGN.page.header.title}>Conta</h1>
          <p className={PERFIL_COMPONENT_DESIGN.page.header.subtitle}>
            Gerencie seus dados pessoais, senha e preferências.
          </p>
        </div>
      </div>

      {/* Tabs + Content */}
      <Tabs defaultValue="general" className="w-full flex flex-col gap-6">
        <TabsList className={PERFIL_COMPONENT_DESIGN.tabs.list}>
          <TabsTrigger value="general" className={PERFIL_COMPONENT_DESIGN.tabs.trigger}>
            <User size={14} />
            Perfil
          </TabsTrigger>
          <TabsTrigger value="change_password" className={PERFIL_COMPONENT_DESIGN.tabs.trigger}>
            <Lock size={14} />
            Senha
          </TabsTrigger>
          <TabsTrigger value="preferencias" className={PERFIL_COMPONENT_DESIGN.tabs.trigger}>
            <Settings size={14} />
            Preferências
          </TabsTrigger>
        </TabsList>

        {/* Perfil */}
        <TabsContent value="general">
          <div className={PERFIL_COMPONENT_DESIGN.section.card}>
            <div className={PERFIL_COMPONENT_DESIGN.section.titleRow}>
              <div className={PERFIL_COMPONENT_DESIGN.section.iconBox}>
                <User size={18} className={PERFIL_COMPONENT_DESIGN.section.icon} />
              </div>
              <div>
                <h3 className={PERFIL_COMPONENT_DESIGN.section.title}>Dados Pessoais</h3>
                <p className={PERFIL_COMPONENT_DESIGN.section.description}>Atualize suas informações de perfil</p>
              </div>
            </div>
            {/* Avatar */}
            <div className={PERFIL_COMPONENT_DESIGN.avatar.container}>
              <div className={PERFIL_COMPONENT_DESIGN.avatar.wrapper}>
                <div
                  className={PERFIL_COMPONENT_DESIGN.avatar.imageContainer}
                  onClick={() => !avatarLoading && fileInputRef.current?.click()}
                >
                  {avatar ? (
                    <img src={avatar} alt="Avatar" className={PERFIL_COMPONENT_DESIGN.avatar.image} />
                  ) : (
                    <User size={32} className={PERFIL_COMPONENT_DESIGN.avatar.placeholder} />
                  )}
                  <div className={PERFIL_COMPONENT_DESIGN.avatar.overlay}>
                    {avatarLoading ? (
                      <Loader2 size={20} className="text-white animate-spin" />
                    ) : (
                      <Camera size={20} className="text-white" />
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadAvatar(file);
                    e.target.value = '';
                  }}
                />
                <div>
                  <p className={PERFIL_COMPONENT_DESIGN.avatar.name}>{nome} {sobrenome}</p>
                  <p className={PERFIL_COMPONENT_DESIGN.avatar.email}>{email}</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={PERFIL_COMPONENT_DESIGN.avatar.changeButton}
                    disabled={avatarLoading}
                  >
                    Alterar foto
                  </button>
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-100 my-1" />

            <PerfilGeral
              nome={nome}
              sobrenome={sobrenome}
              email={email}
              telefone={telefone}
              usuario={maskUsername(usuario)}
              nomeFantasia={nomeFantasia}
              changeTelefone={e => setTelefone(e.target.value)}
              changeNome={e => setNome(e.target.value)}
              changeSobrenome={e => setSobrenome(e.target.value)}
              changeEmail={e => setEmail(e.target.value)}
              changeUsuario={e => setUsuario(e.target.value)}
              changeNomeFantasia={e => setNomeFantasia(e.target.value)}
              submit={handleEditarPerfil}
              isLoading={isLoading}
            />
          </div>
        </TabsContent>

        {/* Senha */}
        <TabsContent value="change_password">
          <div className={PERFIL_COMPONENT_DESIGN.section.card}>
            <div className={PERFIL_COMPONENT_DESIGN.section.titleRow}>
              <div className={PERFIL_COMPONENT_DESIGN.section.iconBox}>
                <Lock size={18} className={PERFIL_COMPONENT_DESIGN.section.icon} />
              </div>
              <div>
                <h3 className={PERFIL_COMPONENT_DESIGN.section.title}>Alterar Senha</h3>
                <p className={PERFIL_COMPONENT_DESIGN.section.description}>Defina uma nova senha para sua conta</p>
              </div>
            </div>
            <PerfilAlterarSenha
              password={password}
              repeatPassword={repeatPassword}
              changePassword={e => setPassword(e.target.value)}
              changeRepeatPassword={e => setRepeatPassword(e.target.value)}
              isLoading={isLoading}
              submit={handleEditarSenha}
            />
          </div>
        </TabsContent>

        {/* Preferências */}
        <TabsContent value="preferencias">
          <div className={PERFIL_COMPONENT_DESIGN.section.card}>
            <div className={PERFIL_COMPONENT_DESIGN.section.titleRow}>
              <div className={PERFIL_COMPONENT_DESIGN.section.iconBox}>
                <Settings size={18} className={PERFIL_COMPONENT_DESIGN.section.icon} />
              </div>
              <div>
                <h3 className={PERFIL_COMPONENT_DESIGN.section.title}>Preferências</h3>
                <p className={PERFIL_COMPONENT_DESIGN.section.description}>Configure notificações e aparência</p>
              </div>
            </div>
            <Preferencias
              user={user}
              cliente={cliente}
              mensagemIntervalo={mensagemIntervalo}
              changeMensagemIntervalo={e => setMensagemIntervalo(e.target.value)}
              isModificado={isModificado}
              changeIsModificado={() => setIsModificado(true)}
              checkedSomNotificacao={soundNotification === 'true' ? true : false}
              changeSomNotificacao={changeSoundNotification}
              onSubmit={handelEditarPreferencias}
              config={config}
              changeConfig={e => {
                const { name, value } = e;
                setIsModificado(true);
                setConfig((prevConfig) => ({
                  ...prevConfig,
                  [name]: {
                    ...prevConfig[name],
                    ...value,
                  },
                }));
              }}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
